import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

// Small celebratory panel that slides in when a new skin is unlocked.
// Renders overlay with a bouncy swatch animation and auto-dismisses on click.
export default function SkinUnlockToast({ skin, onClose }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    const t = setTimeout(() => onClose && onClose(), 6000);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t);
    };
  }, [onClose]);

  if (!skin) return null;

  return (
    <div
      data-testid="skin-unlock-toast"
      className="fixed top-8 left-1/2 -translate-x-1/2 z-[70] pointer-events-auto"
      style={{
        transform: `translate(-50%, ${mounted ? "0" : "-30px"})`,
        opacity: mounted ? 1 : 0,
        transition: "opacity 380ms ease, transform 380ms cubic-bezier(0.2, 1.2, 0.4, 1)",
      }}
    >
      <div
        className="relative flex items-center gap-4 px-6 py-4 bg-black/90 backdrop-blur-xl border rounded-none"
        style={{
          borderColor: skin.color,
          boxShadow: `0 0 32px ${skin.glow}, inset 0 0 24px rgba(0,0,0,0.5)`,
        }}
      >
        {/* Animated swatch */}
        <div className="relative w-14 h-14 shrink-0">
          <div
            className="absolute inset-0"
            style={{
              background: skin.color,
              boxShadow: `0 0 28px ${skin.glow}`,
              animation: "skinPulse 1.4s ease-in-out infinite",
            }}
          />
          <Sparkles
            size={14}
            className="absolute -top-2 -right-2"
            style={{ color: skin.color, filter: `drop-shadow(0 0 6px ${skin.glow})` }}
          />
        </div>

        <div className="min-w-[220px]">
          <div className="text-[10px] tracking-[0.4em] text-white/50">SKIN UNLOCKED</div>
          <div
            className="mt-1 font-display text-2xl tracking-tighter"
            style={{ color: skin.color, textShadow: `0 0 14px ${skin.glow}` }}
          >
            {skin.name}
          </div>
          <div className="text-[10px] tracking-[0.3em] text-white/40 mt-1">
            OPEN THE SKIN PICKER TO EQUIP
          </div>
        </div>

        <button
          data-testid="skin-unlock-dismiss"
          onClick={onClose}
          className="absolute top-1 right-1 p-1 text-white/40 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      <style>{`
        @keyframes skinPulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.08); filter: brightness(1.4); }
        }
      `}</style>
    </div>
  );
}
